'use client';

import { createContext, useContext, useRef, useState, useCallback } from 'react';

const VoskModelContext = createContext();

/**
 * Voskモデルをグローバルで保持するProvider
 * 一度ロードしたモデルはアプリ終了まで再利用される
 */
export function VoskModelProvider({ children }) {
  const [modelStatus, setModelStatus] = useState("idle"); // "idle" | "checking" | "loading" | "loaded" | "error" | "not_loaded"
  const modelRef = useRef(null);
  const isCheckingRef = useRef(false);

  /**
   * モデルのキャッシュ確認と自動ロード
   */
  const checkAndLoadCachedModel = useCallback(async (addLog) => {
    if (isCheckingRef.current) {
      addLog?.("既にモデル確認中です", "Warning");
      return;
    }

    if (modelRef.current) {
      addLog?.("モデルは既にロード済みです", "Information");
      setModelStatus("loaded");
      return;
    }

    isCheckingRef.current = true;
    setModelStatus("checking");

    try {
      addLog?.("🔍 モデルのキャッシュを確認します...", "Information");

      const modelUrl = "/vosk-models/vosk-model-small-en-us-0.15.tar.gz";

      // HEADリクエストでキャッシュの存在を確認
      const response = await fetch(modelUrl, {
        method: 'HEAD',
        cache: 'default'
      });

      if (response.ok) {
        const cacheStatus = response.headers.get('x-cache') ||
                           response.headers.get('cf-cache-status') ||
                           'unknown';

        addLog?.(`✅ モデルファイルが見つかりました (cache-status: ${cacheStatus})`, "Information");
        addLog?.("📥 モデルをロードします...", "Information");

        setModelStatus("loading");

        const voskModule = await import("vosk-browser");
        addLog?.("vosk-browserモジュールを読み込みました", "Information");

        let createModelFunc;
        if (typeof voskModule.createModel === "function") {
          createModelFunc = voskModule.createModel;
        } else if (
          voskModule.default &&
          typeof voskModule.default.createModel === "function"
        ) {
          createModelFunc = voskModule.default.createModel;
        } else {
          throw new Error("createModel関数が見つかりません");
        }

        addLog?.("モデルファイルを読み込んでいます...", "Information");
        modelRef.current = await createModelFunc(modelUrl);

        setModelStatus("loaded");
        addLog?.("✅ モデルをロードしました", "Information");
      } else {
        addLog?.(`⚠️ モデルファイルが見つかりません (status: ${response.status})`, "Warning");
        setModelStatus("not_loaded");
      }
    } catch (error) {
      addLog?.(`⚠️ モデル確認中にエラー: ${error.message}`, "Warning");
      setModelStatus("not_loaded");
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  /**
   * モデルのダウンロード（明示的に呼び出す場合）
   */
  const downloadModel = useCallback(async (addLog) => {
    if (modelRef.current) {
      addLog?.("モデルは既にダウンロード済みです", "Warning");
      setModelStatus("loaded");
      return;
    }

    try {
      addLog?.("モデルのダウンロードを開始します...", "Information");
      setModelStatus("loading");

      const voskModule = await import("vosk-browser");
      addLog?.("vosk-browserモジュールを読み込みました", "Information");

      let createModelFunc;
      if (typeof voskModule.createModel === "function") {
        createModelFunc = voskModule.createModel;
      } else if (
        voskModule.default &&
        typeof voskModule.default.createModel === "function"
      ) {
        createModelFunc = voskModule.default.createModel;
      } else {
        throw new Error("createModel関数が見つかりません");
      }

      addLog?.("モデルファイルを読み込んでいます...", "Information");
      modelRef.current = await createModelFunc(
        "/vosk-models/vosk-model-small-en-us-0.15.tar.gz"
      );

      setModelStatus("loaded");
      addLog?.("✅ モデルのダウンロードが完了しました", "Information");
    } catch (error) {
      setModelStatus("error");
      addLog?.(`❌ モデルのダウンロードに失敗: ${error.message}`, "Error");
    }
  }, []);

  /**
   * モデルの確認（デバッグ用）
   */
  const checkModel = useCallback((addLog) => {
    if (!modelRef.current) {
      addLog?.("モデルがダウンロードされていません", "Warning");
      return;
    }

    addLog?.("モデルの確認を実行します", "Information");
    addLog?.(`モデルオブジェクト: ${typeof modelRef.current}`, "Information");
    addLog?.(
      `KaldiRecognizer: ${typeof modelRef.current.KaldiRecognizer}`,
      "Information"
    );
    addLog?.(
      `createRecognizer: ${typeof modelRef.current.createRecognizer}`,
      "Information"
    );
    addLog?.("✅ モデルの確認が完了しました", "Information");
  }, []);

  return (
    <VoskModelContext.Provider
      value={{
        modelStatus,
        modelRef,
        downloadModel,
        checkModel,
        checkAndLoadCachedModel
      }}
    >
      {children}
    </VoskModelContext.Provider>
  );
}

/**
 * VoskModelContextを使用するためのフック
 */
export function useVoskModelContext() {
  const context = useContext(VoskModelContext);
  if (!context) {
    throw new Error('useVoskModelContext must be used within a VoskModelProvider');
  }
  return context;
}
